import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../config/axiosConfig';
import moment from 'moment';
import DayView from './DayView';
import { weeklyOccurrences, maxOccurrencesFor, formatAmsterdam } from '../utils/time';
import './NewReservation.css';

interface NewReservation {
  bike_id: string;
  start_at: string;
  duration: string;
  reserver_name: string;
  reserver_email: string;
  reserver_phone: string;
  notes: string;
}

interface StartTimeOption {
  start: string;
  display: string;
  max_duration: number;
  available: boolean;
}

interface AvailableTimeSlots {
  bike_id: number;
  date: string;
  business_hours: {
    start: string;
    end: string;
  };
  start_time_options: StartTimeOption[];
}

interface Bike {
  id: number;
  asset_code: string;
  bike_type_name: string;
  location_name: string;
  status: string;
  location_id: number;
}

interface Location {
  id: number;
  name: string;
  city: string;
  // Hoeveel weken vooruit een herhalende reservering bij deze locatie mag lopen.
  max_recurrence_weeks: number;
  // Melding over de huurkosten voor externen en het protocol van de duofiets.
  // Elke locatie stelt dit zelf in; leeg of uit betekent geen melding.
  rental_notice_enabled?: boolean;
  rental_notice_text?: string | null;
  rental_notice_url?: string | null;
}

interface BusyPeriod {
  id: number;
  bike_id: number;
  start_at: string;
  end_at: string;
  status: string;
}

interface CreatedOccurrence {
  id: number;
  start_at: string;
  end_at: string;
  reference_code?: string | null;
}

interface SkippedOccurrence {
  start_at: string;
  end_at: string;
}

interface SeriesResult {
  requested_occurrences: number;
  created: CreatedOccurrence[];
  skipped: SkippedOccurrence[];
}

interface ApiResponse<T> {
  data: T;
  success: boolean;
  error?: string;
  message?: string;
}

/** Standaard aantal keren zodra iemand herhalen aanzet; altijd nog aanpasbaar. */
const DEFAULT_OCCURRENCES = 4;

/**
 * Beheerders vullen de link naar het protocol zelf in. We tonen hem alleen als
 * het een volledig https-adres is, zodat er nooit een `javascript:`-link op deze
 * openbare pagina belandt. De server weigert zo'n adres ook al.
 */
const safeHttpsUrl = (url?: string | null): string | null => {
  const value = String(url || '').trim();
  if (!value) return null;
  try {
    return new URL(value).protocol === 'https:' ? value : null;
  } catch {
    return null;
  }
};

const NewReservation: React.FC = () => {
  const navigate = useNavigate();
  
  // Helper function to format duration in Dutch
  const formatDuration = (hours: number): string => {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    const minuteLabel = minutes === 1 ? '1 minuut' : `${minutes} minuten`;

    if (wholeHours === 0) {
      return minuteLabel;
    }
    if (minutes === 0) {
      return `${wholeHours} uur`;
    }
    return `${wholeHours} uur en ${minuteLabel}`;
  };
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [referenceCode, setReferenceCode] = useState<string | null>(null);
  const [seriesResult, setSeriesResult] = useState<SeriesResult | null>(null);
  const [bikes, setBikes] = useState<Bike[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<AvailableTimeSlots | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(moment().add(1, 'day').format('YYYY-MM-DD'));
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState<boolean>(false);
  const [repeatWeekly, setRepeatWeekly] = useState<boolean>(false);
  const [occurrenceCount, setOccurrenceCount] = useState<number>(DEFAULT_OCCURRENCES);
  // Alle bezette tijden van de gekozen fiets, zodat we vooraf kunnen laten zien
  // welke weken al vol zitten. Bevat geen persoonsgegevens.
  const [busyPeriods, setBusyPeriods] = useState<BusyPeriod[]>([]);
  const [newReservation, setNewReservation] = useState<NewReservation>({
    bike_id: '',
    start_at: '',
    duration: '1',
    reserver_name: '',
    reserver_email: '',
    reserver_phone: '',
    notes: ''
  });

  const fetchLocations = async (): Promise<void> => {
    try {
      const response = await apiClient.get<ApiResponse<Location[]>>('/locations');
      setLocations(response.data.data || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  const fetchBikes = async (): Promise<void> => {
    try {
      const response = await apiClient.get<ApiResponse<Bike[]>>('/bikes');
      setBikes(response.data.data || []);
    } catch (err) {
      console.error('Error fetching bikes:', err);
    }
  };

  const fetchAvailableTimeSlots = async (bikeId: string, date: string): Promise<void> => {
    if (!bikeId || !date) return;
    
    setIsLoadingTimeSlots(true);
    try {
      const params = new URLSearchParams({
        bike_id: bikeId,
        date: date
      });
      
      const response = await apiClient.get<ApiResponse<AvailableTimeSlots>>(`/reservations/slots/available?${params}`);
      setAvailableTimeSlots(response.data.data);
    } catch (err) {
      console.error('Error fetching available time slots:', err);
      setAvailableTimeSlots(null);
    } finally {
      setIsLoadingTimeSlots(false);
    }
  };

  const fetchBusyPeriods = async (bikeId: string): Promise<void> => {
    if (!bikeId) {
      setBusyPeriods([]);
      return;
    }

    try {
      const response = await apiClient.get<ApiResponse<BusyPeriod[]>>(
        `/reservations/bike/${bikeId}/busy`
      );
      setBusyPeriods(response.data.data || []);
    } catch (err) {
      console.error('Error fetching busy periods:', err);
      setBusyPeriods([]);
    }
  };

  const createReservation = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    setSeriesResult(null);
    
     try {
       // Validate that all required fields are filled
       if (!selectedLocation || !newReservation.bike_id || !newReservation.start_at || !newReservation.duration) {
         setError('Vul alle verplichte velden in (locatie, fiets, start tijd en duur).');
         setLoading(false);
         return;
       }
       
       // Validate that user information is filled
       if (!newReservation.reserver_name || !newReservation.reserver_email) {
         setError('Vul uw naam en e-mailadres in.');
         setLoading(false);
         return;
       }
      
      // Validate duration against available time
      if (availableTimeSlots) {
        const selectedStartTime = availableTimeSlots.start_time_options.find(
          option => option.start === newReservation.start_at
        );
        
        if (selectedStartTime) {
          const requestedDuration = parseFloat(newReservation.duration);
          if (requestedDuration > selectedStartTime.max_duration) {
            setError(`Geselecteerde duur (${formatDuration(requestedDuration)}) overschrijdt de maximale beschikbare duur (${formatDuration(selectedStartTime.max_duration)}) voor deze start tijd.`);
            setLoading(false);
            return;
          }
        }
      }
      
      // Calculate end time from start time and duration
      const startTime = moment(newReservation.start_at);
      const durationHours = parseFloat(newReservation.duration);
      const endTime = startTime.clone().add(durationHours, 'hours');
      
      const reservationData = {
        bike_id: parseInt(newReservation.bike_id),
        start_at: startTime.toISOString(),
        end_at: endTime.toISOString(),
        reserver_name: newReservation.reserver_name,
        reserver_email: newReservation.reserver_email,
        reserver_phone: newReservation.reserver_phone,
        notes: newReservation.notes
      };

      if (repeatWeekly && canRepeat) {
        const count = Math.min(Math.max(occurrenceCount, 2), maxOccurrences);
        const response = await apiClient.post<ApiResponse<SeriesResult>>('/reservations/recurring', {
          ...reservationData,
          occurrences: count
        });
        setSeriesResult(response.data.data);
        setSuccess(response.data.message || 'Herhalende reservering aangemaakt!');
      } else {
        const response = await apiClient.post<ApiResponse<any>>('/reservations', reservationData);
        setReferenceCode(response.data.data?.reference_code || null);
        setSuccess('Reservering succesvol aangemaakt!');
      }
      
       // Reset form
       setNewReservation({
         bike_id: '',
         start_at: '',
         duration: '1',
         reserver_name: '',
         reserver_email: '',
         reserver_phone: '',
         notes: ''
       });
       setSelectedLocation('');
       setAvailableTimeSlots(null);
       setIsLoadingTimeSlots(false);
       setRepeatWeekly(false);
       setOccurrenceCount(DEFAULT_OCCURRENCES);
       setBusyPeriods([]);
      
    } catch (err: any) {
      let errorMessage = 'Er is een onbekende fout opgetreden.';
      
      if (err.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (err.response.data && err.response.data.error) {
          if (err.response.data.details && Array.isArray(err.response.data.details)) {
            // Show validation error details
            errorMessage = `Controlefout: ${err.response.data.details.join(', ')}`;
          } else {
            errorMessage = err.response.data.error;
          }
        } else if (err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        } else {
          errorMessage = `Serverfout: ${err.response.status}`;
        }
      } else if (err.request) {
        // The request was made but no response was received
        errorMessage = 'Geen reactie van de server.';
      } else {
        // Something happened in setting up the request that triggered an Error
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
    fetchBikes();
  }, []);

  // Fetch time slots when bike and date are available
  useEffect(() => {
    if (newReservation.bike_id && selectedDate) {
      fetchAvailableTimeSlots(newReservation.bike_id, selectedDate);
    }
  }, [newReservation.bike_id, selectedDate]);

  // De bezette tijden hangen aan de fiets, niet aan de dag, dus één keer ophalen
  // per fiets is genoeg voor de hele vooruitblik.
  useEffect(() => {
    fetchBusyPeriods(newReservation.bike_id);
  }, [newReservation.bike_id]);

  // Filter bikes when location changes
  const filteredBikes = bikes.filter(bike => 
    bike.status === 'available' && 
    (!selectedLocation || bike.location_id.toString() === selectedLocation)
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    setNewReservation(prev => ({
      ...prev,
      [name]: value
    }));

    // Een andere fiets heeft eigen vrije tijden, dus de gekozen tijd vervalt.
    if (name === 'bike_id') {
      setNewReservation(prev => ({
        ...prev,
        start_at: '',
        duration: ''
      }));
      setRepeatWeekly(false);
      if (value) {
        fetchAvailableTimeSlots(value, selectedDate);
      }
    }
    
    // Reset duration when start time changes
    if (name === 'start_at') {
      setNewReservation(prev => ({
        ...prev,
        duration: ''
      }));
    }
  };

  const handleLocationChange = (locationId: string): void => {
    setSelectedLocation(locationId);
    // Reset bike selection when location changes
    setNewReservation(prev => ({
      ...prev,
      bike_id: '',
      start_at: '',
      duration: ''
    }));
    setAvailableTimeSlots(null);
    setIsLoadingTimeSlots(false);
    setRepeatWeekly(false);
  };

  const handleDateChange = (date: string): void => {
    setSelectedDate(date);
    // De starttijden horen bij één dag; op een andere datum bestaat de gekozen
    // tijd niet meer, dus die laten we niet stil blijven staan.
    setNewReservation(prev => ({
      ...prev,
      start_at: '',
      duration: ''
    }));
    setRepeatWeekly(false);
    if (newReservation.bike_id) {
      fetchAvailableTimeSlots(newReservation.bike_id, date);
    }
  };

  const showDayView = Boolean(newReservation.bike_id && selectedDate);
  const selectedDuration = parseFloat(newReservation.duration);
  const hasTimeChosen = Boolean(newReservation.start_at) && !isNaN(selectedDuration) && selectedDuration > 0;
  const daySelection = hasTimeChosen
    ? { start: newReservation.start_at, durationHours: selectedDuration }
    : null;

  const selectedLocationInfo =
    locations.find(location => location.id.toString() === selectedLocation) || null;
  const maxRecurrenceWeeks = selectedLocationInfo?.max_recurrence_weeks ?? 0;

  // De melding over huurkosten en het protocol hoort bij de locatie, dus die kan
  // pas verschijnen zodra er een locatie gekozen is.
  const rentalNoticeText = selectedLocationInfo?.rental_notice_enabled
    ? (selectedLocationInfo.rental_notice_text || '').trim()
    : '';
  const rentalNoticeUrl = selectedLocationInfo?.rental_notice_enabled
    ? safeHttpsUrl(selectedLocationInfo.rental_notice_url)
    : null;
  const showRentalNotice = Boolean(rentalNoticeText || rentalNoticeUrl);

  // Hoeveel keren er bij deze startdatum nog binnen de termijn van de locatie
  // passen. De keuzelijst gaat niet verder, zodat niemand tegen de grens van de
  // server aanloopt.
  const maxOccurrences =
    hasTimeChosen && maxRecurrenceWeeks > 0
      ? maxOccurrencesFor(newReservation.start_at, maxRecurrenceWeeks)
      : 1;
  const canRepeat = hasTimeChosen && maxOccurrences >= 2;

  const occurrencePreview = useMemo(() => {
    if (!repeatWeekly || !canRepeat) return [];

    const start = moment(newReservation.start_at);
    const end = start.clone().add(selectedDuration, 'hours');
    const count = Math.min(Math.max(occurrenceCount, 2), maxOccurrences);

    return weeklyOccurrences(start.toISOString(), end.toISOString(), count).map(occurrence => ({
      ...occurrence,
      // Overlap betekent bezet: begint deze keer voor het einde van een bestaande
      // reservering en eindigt hij erna, dan zitten ze in de weg.
      busy: busyPeriods.some(
        period =>
          moment(occurrence.start_at).isBefore(period.end_at) &&
          moment(occurrence.end_at).isAfter(period.start_at)
      )
    }));
  }, [
    repeatWeekly,
    canRepeat,
    newReservation.start_at,
    selectedDuration,
    occurrenceCount,
    maxOccurrences,
    busyPeriods
  ]);

  const busyOccurrenceCount = occurrencePreview.filter(occurrence => occurrence.busy).length;
  const freeOccurrenceCount = occurrencePreview.length - busyOccurrenceCount;
  const everythingTaken = occurrencePreview.length > 0 && freeOccurrenceCount === 0;

  // Een kortere startdatum of een strengere locatie kan de eerder gekozen reeks
  // te lang maken; dan schuiven we het aantal stil terug naar wat nog mag.
  useEffect(() => {
    if (!repeatWeekly || !canRepeat) return;
    if (occurrenceCount > maxOccurrences) {
      setOccurrenceCount(maxOccurrences);
    }
  }, [repeatWeekly, canRepeat, occurrenceCount, maxOccurrences]);

  return (
    <div className="new-reservation-page">
      <div className="new-reservation-container">
        <div className="header">
          <button 
            className="back-button"
            onClick={() => navigate('/')}
          >
            ← Terug
          </button>
          <h1>Nieuwe Reservering</h1>
          <p>Maak een nieuwe fiets reservering</p>
        </div>

        {error && (
          <div className="error-message">
            <strong>Fout:</strong> {error}
          </div>
        )}

        {success && (
          <div className="success-message">
            <strong>Succes!</strong> {success}

            {seriesResult && (
              <div className="series-result">
                <p className="series-result-title">Gereserveerde datums</p>
                <ul className="occurrence-list">
                  {seriesResult.created.map(occurrence => (
                    <li key={occurrence.id} className="occurrence-item free">
                      <span className="occurrence-mark" aria-hidden="true">✓</span>
                      <span className="occurrence-date">
                        {formatAmsterdam(occurrence.start_at, 'dddd D MMMM YYYY')}
                      </span>
                      <span className="occurrence-time">
                        {formatAmsterdam(occurrence.start_at, 'HH:mm')} -{' '}
                        {formatAmsterdam(occurrence.end_at, 'HH:mm')}
                      </span>
                      {occurrence.reference_code && (
                        <span className="occurrence-reference">{occurrence.reference_code}</span>
                      )}
                    </li>
                  ))}
                </ul>

                {seriesResult.skipped.length > 0 && (
                  <>
                    <p className="series-result-title">
                      Overgeslagen omdat de fiets al bezet was
                    </p>
                    <ul className="occurrence-list">
                      {seriesResult.skipped.map(occurrence => (
                        <li key={occurrence.start_at} className="occurrence-item busy">
                          <span className="occurrence-mark" aria-hidden="true">✕</span>
                          <span className="occurrence-date">
                            {formatAmsterdam(occurrence.start_at, 'dddd D MMMM YYYY')}
                          </span>
                          <span className="occurrence-time">
                            {formatAmsterdam(occurrence.start_at, 'HH:mm')} -{' '}
                            {formatAmsterdam(occurrence.end_at, 'HH:mm')}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <small className="help-text">
                      Wil je die keren toch? Maak er losse reserveringen voor op een andere tijd of
                      een andere fiets.
                    </small>
                  </>
                )}

                <span className="reference-code-hint">
                  Elke keer heeft zijn eigen reserveringsnummer. Bewaar ze, of gebruik de
                  overzichtsmail die we hebben gestuurd.
                </span>
                <button type="button" className="reference-code-done" onClick={() => navigate('/')}>
                  Terug naar de startpagina
                </button>
              </div>
            )}

            {referenceCode && (
              <div className="reference-code-block">
                <span className="reference-code-label">Uw reserveringsnummer</span>
                <span className="reference-code">{referenceCode}</span>
                <span className="reference-code-hint">
                  Bewaar dit nummer. U heeft het nodig om aan te tonen dat deze reservering van u is.
                  We sturen het ook per e-mail.
                </span>
              </div>
            )}
            <button type="button" className="reference-code-done" onClick={() => navigate('/')}>
              Terug naar de startpagina
            </button>
          </div>
        )}

        <form onSubmit={createReservation} className="reservation-form">
          <div className="form-section">
            <h3>📍 Locatie en Fiets</h3>
             <div className="form-row">
               <div className="input-group">
                 <label>Locatie *</label>
                 <select
                   value={selectedLocation}
                   onChange={(e) => handleLocationChange(e.target.value)}
                   required
                 >
                   <option value="">Selecteer een locatie</option>
                   {locations.map(location => (
                     <option key={location.id} value={location.id}>
                       {location.name} - {location.city}
                     </option>
                   ))}
                 </select>
               </div>
               <div className="input-group">
                 <label>Datum *</label>
                 <input
                   type="date"
                   value={selectedDate}
                   onChange={(e) => handleDateChange(e.target.value)}
                   required
                 />
               </div>
             </div>
             
             <div className="form-row">
               <div className="input-group">
                 <label>Fiets *</label>
                 <select
                   name="bike_id"
                   value={newReservation.bike_id}
                   onChange={handleInputChange}
                   required
                   disabled={!selectedLocation}
                 >
                   <option value="">
                     {selectedLocation ? 'Selecteer een fiets' : 'Selecteer eerst een locatie'}
                   </option>
                   {filteredBikes.map(bike => (
                     <option key={bike.id} value={bike.id}>
                       {bike.asset_code} - {bike.bike_type_name}
                     </option>
                   ))}
                 </select>
                 <small className="help-text">
                   {selectedLocation 
                     ? `${filteredBikes.length} beschikbare fietsen op deze locatie`
                     : 'Selecteer eerst een locatie om beschikbare fietsen te zien'
                   }
                 </small>
               </div>
             </div>
           
                     </div>
           
          <div className="form-section">
            <h3>🕐 Tijd Selectie</h3>
            <div className={`time-selection-layout${showDayView ? ' with-day-view' : ''}`}>
              <div className="time-selection-controls">
            {isLoadingTimeSlots ? (
              <div className="loading-time-slots">
                <p>⏳ Beschikbare tijden worden geladen...</p>
              </div>
            ) : availableTimeSlots ? (
               availableTimeSlots.start_time_options.length > 0 ? (
                 <div className="form-row">
                   <div className="input-group">
                     <label>Start Tijd *</label>
                     <select
                       name="start_at"
                       value={newReservation.start_at}
                       onChange={handleInputChange}
                       required
                     >
                       <option value="">Selecteer start tijd</option>
                       {availableTimeSlots.start_time_options
                         .filter(option => option.available)
                         .map((option, index) => (
                           <option key={index} value={option.start}>
                             {option.display} (Max {formatDuration(option.max_duration)})
                           </option>
                         ))}
                     </select>
                     <small className="help-text">
                       Alleen beschikbare tijden worden getoond
                     </small>
                   </div>
                   <div className="input-group">
                     <label>Duur (uren) *</label>
                     <select
                       name="duration"
                       value={newReservation.duration || ''}
                       onChange={handleInputChange}
                       required
                       disabled={!newReservation.start_at}
                     >
                       <option value="">Selecteer eerst een start tijd</option>
                       {(() => {
                         if (!newReservation.start_at || !availableTimeSlots) {
                           return null;
                         }
                         
                         const selectedStartTime = availableTimeSlots.start_time_options
                           .filter(option => option.available)
                           .find(option => option.start === newReservation.start_at);
                         
                         if (!selectedStartTime) {
                           return null;
                         }
                         
                         const durations = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3, 3.25, 3.5, 3.75, 4, 4.25, 4.5, 4.75, 5, 5.25, 5.5, 5.75, 6, 6.25, 6.5, 6.75, 7];
                         return durations
                           .filter(duration => duration <= selectedStartTime.max_duration)
                           .map(duration => (
                             <option key={duration} value={duration.toString()}>
                               {formatDuration(duration)}
                             </option>
                           ));
                       })()}
                     </select>
                     <small className="help-text">
                       Duur is beperkt door beschikbare tijd
                     </small>
                   </div>
                 </div>
               ) : (
                 <div className="no-time-slots">
                   <p>⚠️ Geen beschikbare tijden gevonden voor de geselecteerde datum en fiets.</p>
                   <p>Dit kan betekenen dat:</p>
                   <ul>
                     <li>De fiets is al volledig gereserveerd op deze datum</li>
                     <li>Er zijn geen voldoende grote tijdsgaten tussen bestaande reserveringen</li>
                     <li>Probeer een andere datum of fiets te selecteren</li>
                   </ul>
                 </div>
               )
            ) : newReservation.bike_id && selectedDate ? (
              <div className="no-time-slots">
                <p>⚠️ Geen beschikbare tijden gevonden voor de geselecteerde datum en fiets.</p>
                <p>Probeer een andere datum of fiets te selecteren.</p>
              </div>
            ) : null}
              </div>

              {showDayView && (
                <DayView
                  bikeId={newReservation.bike_id}
                  date={selectedDate}
                  businessHours={availableTimeSlots?.business_hours}
                  selection={daySelection}
                />
              )}
            </div>
          </div>

          <div className="form-section">
            <h3>🔁 Herhalen</h3>

            <label className="repeat-toggle">
              <input
                type="checkbox"
                checked={repeatWeekly}
                disabled={!canRepeat}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setRepeatWeekly(enabled);
                  if (enabled) {
                    setOccurrenceCount(Math.min(DEFAULT_OCCURRENCES, maxOccurrences));
                  }
                }}
              />
              <span>Deze reservering elke week op dezelfde dag en tijd herhalen</span>
            </label>

            {!hasTimeChosen && (
              <small className="help-text">
                Kies eerst een fiets, een start tijd en een duur. Daarna kun je hier aangeven
                dat de reservering zich elke week moet herhalen.
              </small>
            )}

            {hasTimeChosen && !canRepeat && (
              <small className="help-text">
                {selectedLocationInfo
                  ? `Bij ${selectedLocationInfo.name} kun je maximaal ${maxRecurrenceWeeks} ${maxRecurrenceWeeks === 1 ? 'week' : 'weken'} vooruit plannen. Vanaf deze datum past er geen tweede keer meer binnen die termijn, dus kies een eerdere datum.`
                  : 'Kies eerst een locatie.'}
              </small>
            )}

            {repeatWeekly && canRepeat && (
              <>
                <div className="form-row">
                  <div className="input-group">
                    <label>Aantal keren *</label>
                    <select
                      value={occurrenceCount}
                      onChange={(e) => setOccurrenceCount(parseInt(e.target.value, 10))}
                    >
                      {Array.from({ length: maxOccurrences - 1 }, (_unused, index) => index + 2).map(count => (
                        <option key={count} value={count}>
                          {count} keer
                        </option>
                      ))}
                    </select>
                    <small className="help-text">
                      Inclusief de eerste keer. Bij {selectedLocationInfo?.name} kun je maximaal{' '}
                      {maxRecurrenceWeeks} {maxRecurrenceWeeks === 1 ? 'week' : 'weken'} vooruit plannen,
                      dus vanaf deze datum maximaal {maxOccurrences} keer.
                    </small>
                  </div>
                </div>

                {occurrencePreview.length > 0 && (
                  <div className="occurrence-preview">
                    <p className="occurrence-preview-title">
                      {busyOccurrenceCount === 0
                        ? `Alle ${occurrencePreview.length} datums zijn nu vrij:`
                        : `${freeOccurrenceCount} van de ${occurrencePreview.length} datums ${freeOccurrenceCount === 1 ? 'is' : 'zijn'} vrij:`}
                    </p>
                    <ul className="occurrence-list">
                      {occurrencePreview.map(occurrence => (
                        <li
                          key={occurrence.start_at}
                          className={`occurrence-item ${occurrence.busy ? 'busy' : 'free'}`}
                        >
                          <span className="occurrence-mark" aria-hidden="true">
                            {occurrence.busy ? '✕' : '✓'}
                          </span>
                          <span className="occurrence-date">
                            {formatAmsterdam(occurrence.start_at, 'dddd D MMMM YYYY')}
                          </span>
                          <span className="occurrence-time">
                            {formatAmsterdam(occurrence.start_at, 'HH:mm')} -{' '}
                            {formatAmsterdam(occurrence.end_at, 'HH:mm')}
                          </span>
                          {occurrence.busy && (
                            <span className="occurrence-note">al bezet, wordt overgeslagen</span>
                          )}
                        </li>
                      ))}
                    </ul>

                    {everythingTaken ? (
                      <p className="occurrence-warning">
                        Deze fiets is op geen van deze datums vrij. Kies een andere dag, een andere
                        tijd of een andere fiets.
                      </p>
                    ) : (
                      <small className="help-text">
                        Bezette datums slaan we over; de rest wordt wel gereserveerd. Dit is een
                        momentopname: bij het opslaan controleren we het nog een keer, en die
                        uitkomst geldt.
                      </small>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

           <div className="form-section">
             <h3>👤 Persoonlijke Gegevens</h3>
            <div className="form-row">
              <div className="input-group">
                <label>Naam *</label>
                <input
                  type="text"
                  name="reserver_name"
                  value={newReservation.reserver_name}
                  onChange={handleInputChange}
                  required
                  placeholder="Volledige naam"
                />
              </div>
              <div className="input-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="reserver_email"
                  value={newReservation.reserver_email}
                  onChange={handleInputChange}
                  required
                  placeholder="email@voorbeeld.com"
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="input-group">
                <label>Telefoon *</label>
                <input
                  type="tel"
                  name="reserver_phone"
                  value={newReservation.reserver_phone}
                  onChange={handleInputChange}
                  required
                  placeholder="+31612345678"
                />
              </div>
              <div className="input-group">
                <label>Opmerkingen</label>
                <input
                  type="text"
                  name="notes"
                  value={newReservation.notes}
                  onChange={handleInputChange}
                  placeholder="Optionele opmerkingen over de reservering"
                />
              </div>
            </div>
            
            <div className="privacy-notice">
              <small>
                Door deze reservering aan te maken ga je akkoord met onze{' '}
                <a href="https://www.ijsselheem.nl/privacyverklaring" target="_blank" rel="noopener noreferrer">
                  privacyverklaring
                </a>
                . Je gegevens worden alleen gebruikt voor het beheren van je reservering.
              </small>
            </div>

            {showRentalNotice && (
              <div className="rental-notice">
                <small>
                  {rentalNoticeText}
                  {rentalNoticeUrl && (
                    <>
                      {rentalNoticeText ? ' ' : null}
                      <a href={rentalNoticeUrl} target="_blank" rel="noopener noreferrer">
                        Bekijk het protocol voor de duofiets
                      </a>
                    </>
                  )}
                </small>
              </div>
            )}
          </div>
          
          <div className="form-actions">
            <button 
              type="button"
              className="button secondary"
              onClick={() => navigate('/')}
              disabled={loading}
            >
              Annuleren
            </button>
            <button 
              type="submit" 
              className="button primary"
              disabled={loading || (repeatWeekly && everythingTaken)}
            >
              {loading
                ? 'Aanmaken...'
                : repeatWeekly && canRepeat
                  ? `Herhalende Reservering Aanmaken (${freeOccurrenceCount} ${freeOccurrenceCount === 1 ? 'keer' : 'keren'})`
                  : 'Reservering Aanmaken'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewReservation;

